import type { ClientHomePlanFilters } from "api-types/homePlans/homePlanRequests";
import type { JobType } from "api-types/jobs/jobTypes";
import type { LocationLevelType } from "api-types/locations/locationLevelTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import { objectToQueryParams } from "common/utils/fetchHelpers";

// utility functions for dynamically constructing API endpoint urls

const baseAPIPath = `${WEB_API_BASE_PATH}/`;

export function getAdminRateLimiterSettingsURL(): string {
  return `${baseAPIPath}admin-settings/rate-limiter`;
}

export function getAdminRateLimiterSettingsResetURL(): string {
  return `${baseAPIPath}admin-settings/rate-limiter/reset`;
}

export function getAdminFeatureFlagSyncURL(): string {
  return `${baseAPIPath}admin-settings/feature-flags/sync`;
}

export function getAdminMaintenanceSettingsURL(): string {
  return `${baseAPIPath}admin-settings/maintenance`;
}

export function getMaintenanceStatusURL(): string {
  return `${baseAPIPath}maintenance/status`;
}

export function getFeatureFlagsURL(params?: { includeRetired?: boolean }): string {
  return addParamsToURL(`${baseAPIPath}feature-flags`, {
    includeRetired: params?.includeRetired ? "true" : undefined,
  });
}

export function getFeatureFlagPublicDataURL(params?: { builderId?: string }): string {
  return addParamsToURL(`${baseAPIPath}feature-flags/public-data`, {
    builderId: params?.builderId,
  });
}

export function getFeatureFlagGroupsURL(): string {
  return `${baseAPIPath}feature-flags/groups`;
}

export function getFeatureFlagGroupURL(groupId: string): string {
  return `${baseAPIPath}feature-flags/groups/${groupId}`;
}

export function getFeatureFlagGroupMembersURL(groupId: string): string {
  return `${getFeatureFlagGroupURL(groupId)}/members`;
}

export function getFeatureFlagURL(key: string): string {
  return `${baseAPIPath}feature-flags/${key}`;
}

export function getFeatureFlagEnvironmentOverrideURL(key: string, environment: string): string {
  return `${baseAPIPath}feature-flags/${key}/environment-overrides/${environment}`;
}

export function getFeatureFlagEditorAllowlistURL(key: string): string {
  return `${baseAPIPath}feature-flags/${key}/editor-allowlist`;
}

/**
 * BUILDERS
 */

const baseBuilderURL = `${baseAPIPath}builders/`;

/**
 * - URL: /builders/:builderId
 * - CONTROLLER: BuildersController.getOne
 * - RESPONSE TYPE: <ClientBuilder>
 * @param builderId encoded builder id
 * @returns string
 */
export function getBuilderURL(builderId: string) {
  return `${baseBuilderURL}${builderId}`;
}

export function getBuilderSSOConfigURL(builderId: string): string {
  return `${baseBuilderURL}${builderId}/sso`;
}

export function getBuilderSSODiagnosticsURL(builderId: string): string {
  return `${baseBuilderURL}${builderId}/sso/diagnostics`;
}

export function getBuilderSSOAdminDiagnosticsURL(builderId: string): string {
  return `${baseBuilderURL}${builderId}/sso/admin/diagnostics`;
}

export function getBuilderShowroomEmailPreviewURL(builderId: string) {
  return `${getBuilderURL(builderId)}/showroom-email-preview`;
}

export function getBuilderAccountBrochureSourcesURL({ builderId }: { builderId: string }) {
  return `${baseBuilderURL}${builderId}/account/brochure-sources`;
}

export function getBuilderAccountMasterPlanBrochureSourceURL({
  builderId,
  masterPlanId,
}: {
  builderId: string;
  masterPlanId: string;
}) {
  return `${baseBuilderURL}${builderId}/account/master-plans/${masterPlanId}/brochure-source`;
}

/**
 * @description
 * - URL: /builders/:builderId/plan-gltf
 * - CONTROLLER: BuildersController#postPlanGLTF
 * - METHOD: POST
 * - RESPONSE TYPE: <ClientBuilderPlanGltfResponse>
 * @param builderId encoded builder id
 */
export function getBuilderPlanGltfURL(builderId: string) {
  return `${baseBuilderURL}${builderId}/plan-gltf`;
}

/**
 * @description
 * - URL: /builders/:builderId/plan-gltf-option-change
 * - CONTROLLER: BuildersController#postPlanGLTFOptionChange
 * - METHOD: POST
 * - RESPONSE TYPE: <ClientBuilderPlanGltfOptionChangeResponse>
 * @param builderId encoded builder id
 */
export function getBuilderPlanGltfOptionChangeURL(builderId: string) {
  return `${baseBuilderURL}${builderId}/plan-gltf-option-change`;
}

/**
 * @description
 * - URL: /builders
 * - CONTROLLER: BuildersController#getMany
 * - RESPONSE TYPE: <ClientPaginatedResponse<Builder>>
 * @param params query parameters for filtering and pagination
 * @param params.enabledProducts Optional filter for builders with specific enabled products (e.g., 'studio')
 * @param params.limit Optional maximum number of builders to return
 * @returns string
 */
export function getBuildersByEnabledProductsPaginatedURL(params: { enabledProducts?: string; limit?: number }): string {
  return addParamsToURL(`${baseAPIPath}builders`, {
    enabledProducts: params.enabledProducts,
    limit: params.limit?.toString(),
  });
}

/**
 * CUSTOMERS
 */
export function getCustomerURL(builderId: string, customerId: string): string {
  return `${baseBuilderURL}${builderId}/customers/${customerId}`;
}

/**
 * HOME PLAN CONTRACTS
 */

/**
 * - URL: /builders/:builderId/home-plans/:homePlanId/contracts
 * - CONTROLLER: NestedHomePlanContractsController#listByHomePlan, #createOne
 * - RESPONSE TYPE: <ClientPaginatedResponse<ClientHomePlanContractHydrated>>
 */
export function getHomePlanContractsURL(builderId: string, homePlanId: string): string {
  return `${baseBuilderURL}${builderId}/home-plans/${homePlanId}/contracts`;
}

/**
 * - URL: /builders/:builderId/contracts/:contractId
 * - CONTROLLER: HomePlanContractsController#getOne, #updateOne, #deleteOne
 * - RESPONSE TYPE: <ClientHomePlanContractHydrated>
 */
export function getHomePlanContractURL(builderId: string, contractId: string): string {
  return `${baseBuilderURL}${builderId}/contracts/${contractId}`;
}

/**
 * - URL: /builders/:builderId/home-plans/:homePlanId/contracts/public
 * - CONTROLLER: NestedHomePlanContractsController#capturePublicProspect
 * - RESPONSE TYPE: <ClientPublicHomePlanContractCaptureResponse>
 * - Unauthenticated.
 */
export function getPublicHomePlanContractCaptureURL(builderId: string, homePlanId: string): string {
  return `${getHomePlanContractsURL(builderId, homePlanId)}/public`;
}

/**
 * LOCATIONS
 */

/**
 * - URL: /builders/:builderId/locations-internal
 * - CONTROLLER: LocationsController#getMany
 * - RESPONSE TYPE: <ClientLocationsPortalData>
 * @param builderId encoded builder id
 * @returns string
 */
export function getBuilderLocationsURL({ builderId }: { builderId: string }) {
  return `${baseBuilderURL}${builderId}/locations-internal`;
}

/**
 * - URL: /builders/:builderId/locations-internal/types-in-use
 * - CONTROLLER: LocationsController#getLocationTypesInUse
 * - RESPONSE TYPE: <ClientLocationTypesInUse>
 * @param builderId encoded builder id
 * @returns string
 */
export function getBuilderLocationTypesInUseURL({ builderId }: { builderId: string }) {
  return `${baseBuilderURL}${builderId}/locations-internal/types-in-use`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/paginated
 * - CONTROLLER: LocationsController#getManyPaginated
 * - RESPONSE TYPE: <ClientPaginatedResponse<ClientLocationWithLevel>>
 * @param builderId encoded builder id
 * @param paginationParams pagination parameters
 * @returns string
 */
export function getBuilderLocationsPaginatedURL({
  builderId,
  maxDepth,
  pagination,
  onlyOrphans,
  locationLevel,
  includeArchived,
}: {
  builderId: string;
  maxDepth?: number;
  pagination?: PaginationParams;
  onlyOrphans?: boolean;
  locationLevel?: LocationLevelType;
  includeArchived?: boolean;
}) {
  return addParamsToURL(`${baseBuilderURL}${builderId}/locations-internal/paginated`, {
    maxDepth: maxDepth?.toString(),
    ...(pagination ? convertPaginationParams(pagination) : undefined),
    ...(onlyOrphans && { onlyOrphans: "true" }),
    ...(includeArchived !== undefined ? { includeArchived: includeArchived.toString() } : undefined),
    ...(locationLevel && { locationLevel }),
  });
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId
 * - CONTROLLER: LocationsController#getOne
 * - RESPONSE TYPE: <ClientLocationWithLevel>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @returns string
 */

export function getBuilderLocationURL({ builderId, locationId }: { builderId: string; locationId: string }) {
  return `${baseBuilderURL}${builderId}/locations-internal/${locationId}`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/brochure-sources
 * - CONTROLLER: LocationsController#getBrochureSources
 * - RESPONSE TYPE: <ClientLocationPlanBrochureSourcesData>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @returns string
 */
export function getBuilderLocationBrochureSourcesURL({
  builderId,
  locationId,
}: {
  builderId: string;
  locationId: string;
}) {
  return `${getBuilderLocationURL({ builderId, locationId })}/brochure-sources`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/location-plans/:locationPlanId/brochure-source
 * - CONTROLLER: LocationPlansController#putBrochureSource, LocationPlansController#deleteBrochureSource
 * - RESPONSE TYPE: <ClientLocationPlanBrochureSourcesData>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param locationPlanId encoded location plan id
 * @returns string
 */
export function getBuilderLocationPlanBrochureSourceURL({
  builderId,
  locationId,
  locationPlanId,
}: {
  builderId: string;
  locationId: string;
  locationPlanId: string;
}) {
  return `${getBuilderLocationURL({ builderId, locationId })}/location-plans/${locationPlanId}/brochure-source`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/summary
 * - CONTROLLER: LocationsController#getBuilderSummary
 * - RESPONSE TYPE: <ClientLocationSummaryData>
 * @param builderId encoded builder id
 * @param refresh optional boolean to request fresh summary data
 * @returns string
 */
export function getBuilderLocationsSummaryURL({ builderId, refresh }: { builderId: string; refresh?: boolean }) {
  return addParamsToURL(`${baseBuilderURL}${builderId}/locations-internal/summary`, {
    refresh: refresh?.toString(),
  });
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/summary
 * - CONTROLLER: LocationsController#getLocationSummary
 * - RESPONSE TYPE: <ClientLocationSummaryData>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param includeChildren optional boolean to include child locations
 * @param refresh optional boolean to request fresh summary data
 * @returns string
 */
export function getBuilderLocationSummaryURL({
  builderId,
  locationId,
  includeChildren,
  refresh,
}: {
  builderId: string;
  locationId: string;
  includeChildren?: boolean;
  refresh?: boolean;
}) {
  return addParamsToURL(`${getBuilderLocationURL({ builderId, locationId })}/summary`, {
    includeChildren: includeChildren?.toString(),
    refresh: refresh?.toString(),
  });
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/public-map-data
 * - CONTROLLER: LocationsController#getPublicMapData
 * - RESPONSE TYPE: <ClientLocationPublicMapData>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @returns string
 */
export function getBuilderLocationPublicMapURL({ builderId, locationId }: { builderId: string; locationId: string }) {
  return `${getBuilderLocationURL({ builderId, locationId })}/public-map-data`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/base-data
 * - CONTROLLER: LocationsController#getBaseData
 * - RESPONSE TYPE: <ClientLocationBaseData>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @returns string
 */
export function getBuilderLocationBaseDataURL({ builderId, locationId }: { builderId: string; locationId: string }) {
  return `${getBuilderLocationURL({ builderId, locationId })}/base-data`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/parents
 * - CONTROLLER: LocationsController#getParents
 * - RESPONSE TYPE: <ClientLocationWithLevel[]>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @returns string
 */
export function getBuilderLocationParentsURL({ builderId, locationId }: { builderId: string; locationId: string }) {
  return `${getBuilderLocationURL({ builderId, locationId })}/parents`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/children
 * - CONTROLLER: LocationsController#getChildren
 * - RESPONSE TYPE: <ClientLocationWithLevel[]>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @returns string
 */
export function getBuilderLocationChildrenURL({
  builderId,
  locationId,
  includeArchived,
}: {
  builderId: string;
  locationId: string;
  includeArchived?: boolean;
}) {
  return addParamsToURL(`${getBuilderLocationURL({ builderId, locationId })}/children`, {
    includeArchived: includeArchived?.toString(),
  });
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/home-plans
 * - CONTROLLER: LocationsController#getHomePlans
 * - RESPONSE TYPE: <ClientPaginatedResponse<<ClientHomePlan[]>>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param includeChildren optional boolean to include child locations
 * @param pagination optional pagination parameters
 * @param filters optional filters for phase, category, lot status, etc.
 * @returns string
 */
export function getBuilderLocationHomePlansURL({
  builderId,
  locationId,
  pagination,
  includeChildren,
  filters,
  excludeMultiUnitSection,
}: {
  builderId: string;
  locationId: string;
  pagination?: PaginationParams;
  includeChildren?: boolean;
  filters?: ClientHomePlanFilters;
  excludeMultiUnitSection?: boolean;
}) {
  // Only include filters param if there are active filters
  const hasActiveFilters =
    filters &&
    (filters.phase?.length ||
      filters.category?.length ||
      filters.lotStatus?.length ||
      filters.lotType?.length ||
      filters.source?.length ||
      filters.locked !== undefined ||
      filters.detached !== undefined);

  return addParamsToURL(`${getBuilderLocationURL({ builderId, locationId })}/home-plans`, {
    includeChildren: includeChildren?.toString(),
    ...(pagination ? convertPaginationParams(pagination) : undefined),
    ...(hasActiveFilters ? { filters: JSON.stringify(filters) } : undefined),
    ...(excludeMultiUnitSection ? { excludeMultiUnitSection: "true" } : undefined),
  });
}

export function getBuilderLocationMultiUnitHomePlansURL({
  builderId,
  locationId,
}: {
  builderId: string;
  locationId: string;
}) {
  return `${getBuilderLocationURL({ builderId, locationId })}/multi-unit-home-plans`;
}

/**
 * MASTER PLANS
 */

/**
 * @description
 * - URL: /builders/:builderId/master-plans
 * - CONTROLLER: MasterPlansController#getMany
 * - RESPONSE TYPE: <ClientMasterPlan[]>
 * @param builderId encoded builder id
 * @param detached optional boolean to filter detached plans
 * @param masterPlanId optional master plan id
 * @param includeArchived optional boolean to include archived plans
 * @returns string
 */
export function getBuilderMasterPlansURL({
  builderId,
  detached,
  masterPlanId,
  includeArchived,
}: {
  builderId: string;
  detached?: boolean;
  masterPlanId?: string;
  includeArchived?: boolean;
}): string {
  let url = `${baseBuilderURL}${builderId}/master-plans`;

  if (masterPlanId) {
    url += `/${masterPlanId}`;
  }

  return addParamsToURL(url, {
    detached: detached?.toString(),
    ...(includeArchived === true ? { archived: "true" } : {}),
  });
}

/**
 * @description
 * - URL: /builders/:builderId/jobs
 * - CONTROLLER: JobsController#getMany
 * - RESPONSE TYPE: <ClientPaginatedResponse<ClientJobHydrated>>
 *
 * The controller accepts dynamic `argNameN`/`argValueN` filter pairs (encoded
 * from `requestArgs`) plus the standard list options (limit/offset/order).
 */
export function getBuilderJobsURL({
  builderId,
  type,
  requestArgs,
  orderBy,
  order,
  limit,
  offset,
}: {
  builderId: string;
  type?: JobType;
  requestArgs?: Record<string, string>;
  orderBy?: string;
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
}): string {
  const url = `${baseBuilderURL}${builderId}/jobs`;
  const requestArgParams: Record<string, string> = {};
  if (requestArgs) {
    Object.entries(requestArgs).forEach(([name, value], i) => {
      requestArgParams[`argName${i + 1}`] = name;
      requestArgParams[`argValue${i + 1}`] = value;
    });
  }
  return addParamsToURL(url, {
    limit: limit !== undefined ? String(limit) : undefined,
    offset: offset !== undefined ? String(offset) : undefined,
    type,
    orderBy,
    order,
    ...requestArgParams,
  });
}
/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/location-workbooks
 * - CONTROLLER: LocationWorkbooksController#getEffective
 * - RESPONSE TYPE: <ClientLocationWorkbook>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @returns string
 */
export function getBuilderLocationWorkbookURL({ builderId, locationId }: { builderId: string; locationId: string }) {
  return `${getBuilderLocationURL({ builderId, locationId })}/location-workbooks`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/location-studio-docs
 * - CONTROLLER: LocationStudioDocsController#getEffective
 * - RESPONSE TYPE: <ClientLocationStudioDoc>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @returns string
 */
export function getBuilderLocationStudioDocsURL({ builderId, locationId }: { builderId: string; locationId: string }) {
  return `${getBuilderLocationURL({ builderId, locationId })}/location-studio-docs`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/location-plans
 * - CONTROLLER: LocationPlansController#getMany
 * - RESPONSE TYPE: <ClientLocationPlanListData>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param detached optional boolean to filter detached plans
 * @param masterPlanId optional master plan id
 * @param includeArchived optional boolean to include archived plans (defaults to false)
 * @param includeChildren optional boolean to also search descendant locations, returning only each
 * chain's leaf plan; the server ignores it for users without child location access
 * @returns string
 */

export function getBuilderLocationPlansURL({
  builderId,
  locationId,
  detached,
  masterPlanId,
  includeArchived,
  includeChildren,
}: {
  builderId: string;
  locationId: string;
  detached?: boolean;
  masterPlanId?: string;
  includeArchived?: boolean;
  includeChildren?: boolean;
}) {
  return addParamsToURL(`${getBuilderLocationURL({ builderId, locationId })}/location-plans`, {
    detached: detached?.toString(),
    masterPlanId,
    ...(includeArchived === true ? { archived: "true" } : {}),
    ...(includeChildren === true ? { includeChildren: "true" } : {}),
    ...convertPaginationParams({ limit: 1000 }),
  });
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/option-catalog-history
 * - CONTROLLER: LocationOptionCatalogHistoryController#getHistory
 * - RESPONSE TYPE: <ClientPaginatedResponse<ClientOptionChangeEvent>>
 */
export function getLocationOptionCatalogHistoryURL(
  { builderId, locationId }: { builderId: string; locationId: string },
  filters?: {
    startDate?: string;
    endDate?: string;
    planId?: string;
    optionSetId?: string;
    changeType?: string;
  },
): string {
  return addParamsToURL(`${getBuilderLocationURL({ builderId, locationId })}/option-catalog-history`, {
    startDate: filters?.startDate,
    endDate: filters?.endDate,
    planId: filters?.planId,
    optionSetId: filters?.optionSetId,
    changeType: filters?.changeType,
  });
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/option-catalog-history/filters
 * - CONTROLLER: LocationOptionCatalogHistoryController#getFilters
 * - RESPONSE TYPE: <ClientLocationOptionCatalogHistoryFilters>
 */
export function getLocationOptionCatalogHistoryFiltersURL({
  builderId,
  locationId,
  planId,
}: {
  builderId: string;
  locationId: string;
  planId?: string;
}): string {
  return addParamsToURL(`${getBuilderLocationURL({ builderId, locationId })}/option-catalog-history/filters`, {
    planId,
  });
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/location-plans/:locationPlanId
 * - CONTROLLER: LocationPlansController#getOne
 * - RESPONSE TYPE: <ClientLocationPlanBaseData>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param locationPlanId encoded location plan id
 * @returns string
 */
export function getBuilderLocationPlanURL({
  builderId,
  locationId,
  locationPlanId,
}: {
  builderId: string;
  locationId: string;
  locationPlanId: string;
}) {
  return `${getBuilderLocationURL({ builderId, locationId })}/location-plans/${locationPlanId}`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/location-plans/:locationPlanId/home-plans
 * - CONTROLLER: LocationPlansController#getHomePlans
 * - RESPONSE TYPE: <ClientHomePlanHydratedNoDoc[]>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param locationPlanId encoded location plan id
 * @returns string
 */
export function getLocationPlanHomePlansURL({
  builderId,
  locationId,
  locationPlanId,
}: {
  builderId: string;
  locationId: string;
  locationPlanId: string;
}) {
  return `${getBuilderLocationPlanURL({ builderId, locationId, locationPlanId })}/home-plans`;
}

/**
 * @description - an unscoped endpoint for fetching a location plan without location id
 * - URL: /builders/:builderId/location-plans?locationPlanId=:locationPlanId
 * - CONTROLLER: BuildersController#getLocationPlan
 * - RESPONSE TYPE: <ClientLocationPlanHydrated>
 * @param builderId encoded builder id
 * @param locationPlanId encoded location plan id
 * @param studioDocVersionId optional encoded studio doc version id — hydrates doc from that snapshot with location plan option templates
 * @returns string
 */
export function getUnscopedLocationPlanURL({
  builderId,
  locationPlanId,
  studioDocVersionId,
}: {
  builderId: string;
  locationPlanId: string;
  studioDocVersionId?: string;
}) {
  const base = `${getBuilderURL(builderId)}/location-plans`;
  const params = new URLSearchParams({ locationPlanId });
  if (studioDocVersionId) {
    params.set("studioDocVersionId", studioDocVersionId);
  }
  return `${base}?${params.toString()}`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/lots
 * - CONTROLLER: LotsController#getMany
 * - RESPONSE TYPE: <ClientPaginatedResponse<ClientLotHydrated>>, or
 *   <ClientPaginatedResponse<ClientLotWithPlanCounts>> with `includePlanCounts`
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param locationPlanId optional location plan id
 * @param pagination optional pagination parameters
 * @param includePlanCounts request lots carrying only their home-plan and lot-plan counts instead of
 * the plans themselves. Rejected alongside `locationPlanId`, which has no count variant.
 * @returns string
 */
export function getBuilderLotsURL({
  builderId,
  locationId,
  locationPlanId,
  pagination,
  includePlanCounts,
}: {
  builderId: string;
  locationId: string;
  locationPlanId?: string;
  pagination?: PaginationParams;
  includePlanCounts?: boolean;
}): string {
  return addParamsToURL(`${getBuilderLocationURL({ builderId, locationId })}/lots`, {
    ...(pagination ? convertPaginationParams(pagination) : undefined),
    locationPlanId,
    includePlanCounts: includePlanCounts ? "true" : undefined,
  });
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/lots/children
 * - CONTROLLER: LotsController#getManyWithChildren
 * - RESPONSE TYPE: <ClientPaginatedResponse<ClientLotHydrated>>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param pagination optional pagination parameters
 * @returns string
 */
export function getBuilderLotsWithChildrenURL({
  builderId,
  locationId,
  pagination,
}: {
  builderId: string;
  locationId: string;
  pagination?: PaginationParams;
}): string {
  return addParamsToURL(`${getBuilderLocationURL({ builderId, locationId })}/lots/children`, {
    ...(pagination ? convertPaginationParams(pagination) : undefined),
  });
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/lots/:lotId
 * - CONTROLLER: LotsController#getOne
 * - RESPONSE TYPE: <ClientLotHydrated>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param lotId encoded lot id
 * @returns string
 */
export function getBuilderLotURL({
  builderId,
  lotId,
  locationId,
}: {
  builderId: string;
  lotId: string;
  locationId: string;
}): string {
  return `${getBuilderLocationURL({ builderId, locationId })}/lots/${lotId}`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/lots/:lotId/home-plans
 * - CONTROLLER: LotsController#getHomePlans
 * - RESPONSE TYPE: <ClientHomePlan[]>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param lotId encoded lot id
 * @returns string
 */
export function getBuilderLotHomePlansURL({
  builderId,
  locationId,
  lotId,
}: {
  builderId: string;
  locationId: string;
  lotId: string;
}) {
  return `${getBuilderLotURL({ builderId, locationId, lotId })}/home-plans`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/lots/:lotId/lot-community-plans
 * - CONTROLLER: LotsController#getCommunityPlans
 * - RESPONSE TYPE: <ClientCommunityPlan[]>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param lotId encoded lot id
 * @returns string
 */
export function getLotCommunityPlansURL({
  builderId,
  locationId,
  lotId,
}: {
  builderId: string;
  locationId: string;
  lotId: string;
}): string {
  return `${baseBuilderURL}${builderId}/communities/${locationId}/lots/${lotId}/lot-community-plans`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/lots/:lotId/lot-community-plans/fetch
 * - CONTROLLER: LotCommunityPlansController#getOneByCommunityId
 * - RESPONSE TYPE: <ClientLotCommunityPlanHydrated>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param lotId encoded lot id
 * @param communityPlanId encoded community plan id
 * @returns string
 */
export function getLotCommunityPlanByCommunityPlanIdURL({
  builderId,
  locationId,
  lotId,
  communityPlanId,
}: {
  builderId: string;
  locationId: string;
  lotId: string;
  communityPlanId: string;
}): string {
  return `${getLotCommunityPlansURL({ builderId, locationId, lotId })}/fetch?communityPlanId=${communityPlanId}`;
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/lots/valid-move-targets?communityId=:communityId
 * - CONTROLLER: LotsController#getValidMoveTargets
 * - RESPONSE TYPE: <ValidMoveTarget[]>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param communityId encoded community id
 * @returns string
 */
export function getLotValidMoveTargetsURL({
  builderId,
  locationId,
  communityId,
}: {
  builderId: string;
  locationId: string;
  communityId: string;
}): string {
  return addParamsToURL(`${getBuilderLocationURL({ builderId, locationId })}/lots/valid-move-targets`, {
    communityId,
  });
}

/**
 * @description
 * - URL: /builders/:builderId/locations-internal/:locationId/lots/bulk-move
 * - CONTROLLER: LotsController#bulkMoveLots
 * - RESPONSE TYPE: <LotBulkMoveResponse>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @returns string
 */
export function getLotBulkMoveURL({ builderId, locationId }: { builderId: string; locationId: string }): string {
  return `${getBuilderLocationURL({ builderId, locationId })}/lots/bulk-move`;
}

/**
 * DRAWING INTAKE
 * All routes under /api/web/v1/builders/:builderId/drawing-intake/...
 * Create job: POST sources/:sourceId/jobs (use postBuilderDrawingIntakeJobURL).
 */
export function getBuilderDrawingIntakeSourcesURL({ builderId }: { builderId: string }): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sources`;
}

/** GET/PUT/DELETE a single source. */
export function getBuilderDrawingIntakeSourceByIdURL({
  builderId,
  sourceId,
}: {
  builderId: string;
  sourceId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sources/${sourceId}`;
}

export function getBuilderDrawingIntakeSheetURL({
  builderId,
  sheetId,
}: {
  builderId: string;
  sheetId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sheets/${sheetId}`;
}

/** GET/PUT/DELETE a single drawing */
export function getBuilderDrawingIntakeSheetDrawingURL({
  builderId,
  sheetId,
}: {
  builderId: string;
  sheetId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sheets/${sheetId}/drawings`;
}

/** DELETE to clean all jobs and stages for a source. */
export function getBuilderDrawingIntakeSourceCleanJobsURL({
  builderId,
  sourceId,
}: {
  builderId: string;
  sourceId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sources/${sourceId}/clean-jobs`;
}

export function getBuilderDrawingIntakeDrawingURL({
  builderId,
  drawingId,
}: {
  builderId: string;
  drawingId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/drawings/${drawingId}`;
}

/** GET a drawing by its studioLiteDrawingId. */
export function getBuilderDrawingIntakeDrawingByStudioLiteDrawingIdURL({
  builderId,
  studioLiteDrawingId,
}: {
  builderId: string;
  studioLiteDrawingId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/drawings/by-studio-lite-drawing-id/${studioLiteDrawingId}`;
}

export function getBuilderDrawingIntakeLayerURL({
  builderId,
  layerId,
}: {
  builderId: string;
  layerId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/layers/${layerId}`;
}

export function postBuilderDrawingIntakeSheetLayerURL({
  builderId,
  sheetId,
}: {
  builderId: string;
  sheetId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sheets/${sheetId}/layers`;
}

export function getBuilderDrawingIntakeSheetLayerURL({
  builderId,
  sheetId,
  layerId,
}: {
  builderId: string;
  sheetId: string;
  layerId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sheets/${sheetId}/layers/${layerId}`;
}

/** DELETE to clean all sheets, sheet layers, drawings, and layers for a source. */
export function getBuilderDrawingIntakeSourceCleanResultsURL({
  builderId,
  sourceId,
}: {
  builderId: string;
  sourceId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sources/${sourceId}/clean-results`;
}

/** DELETE to clean all jobs, sheets, drawings, and layers for a source (source is left in place). */
export function getBuilderDrawingIntakeSourceCleanURL({
  builderId,
  sourceId,
}: {
  builderId: string;
  sourceId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sources/${sourceId}/clean`;
}

export function getBuildingDrawingIntakeJobBySourceIdURL({
  builderId,
  sourceId,
}: {
  builderId: string;
  sourceId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sources/${sourceId}/jobs`;
}

export function postBuilderDrawingIntakeStageURL({ builderId, jobId }: { builderId: string; jobId: string }): string {
  return `${getBuilderURL(builderId)}/drawing-intake/jobs/${jobId}/stages`;
}

/** GET sheets for a source: /sources/:sourceId/sheets */
export function getBuilderDrawingIntakeSourceSheetsURL({
  builderId,
  sourceId,
}: {
  builderId: string;
  sourceId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sources/${sourceId}/sheets`;
}

/** POST create a sheet under a source: /sources/:sourceId/sheets */
export function postBuilderDrawingIntakeSourceSheetURL({
  builderId,
  sourceId,
}: {
  builderId: string;
  sourceId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sources/${sourceId}/sheets`;
}

/** PUT a sheet: PUT sheets/:sheetId */
export function putBuilderDrawingIntakeSheetURL({
  builderId,
  sheetId,
}: {
  builderId: string;
  sheetId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sheets/${sheetId}`;
}

/** POST create a drawing under a sheet: /sheets/:sheetId/drawings */
export function postBuilderDrawingIntakeDrawingURL({
  builderId,
  sheetId,
}: {
  builderId: string;
  sheetId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sheets/${sheetId}/drawings`;
}

/** GET list of jobs; create job via postBuilderDrawingIntakeJobURL (POST sources/:sourceId/jobs). */
export function getBuilderDrawingIntakeJobsURL({ builderId }: { builderId: string }): string {
  return `${getBuilderURL(builderId)}/drawing-intake/jobs`;
}

export function getBuildingDrawingIntakeJobByIdURL({ builderId, jobId }: { builderId: string; jobId: string }): string {
  return `${getBuilderURL(builderId)}/drawing-intake/jobs/${jobId}`;
}

export function postBuilderDrawingIntakeJobURL({
  builderId,
  sourceId,
}: {
  builderId: string;
  sourceId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/sources/${sourceId}/jobs`;
}

/** GET/POST stages for a job: /jobs/:jobId/stages */
export function getBuilderDrawingIntakeJobStagesURL({
  builderId,
  jobId,
}: {
  builderId: string;
  jobId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/jobs/${jobId}/stages`;
}

/** GET/PUT a single stage: /stages/:stageId */
export function getBuilderDrawingIntakeStagesURL({
  builderId,
  stageId,
}: {
  builderId: string;
  stageId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/stages/${stageId}`;
}

export function putBuilderDrawingIntakeStageUpdateURL({
  builderId,
  stageId,
}: {
  builderId: string;
  stageId: string;
}): string {
  return `${getBuilderDrawingIntakeStagesURL({ builderId, stageId })}`;
}

export function putBuilderDrawingIntakeStageStartURL({
  builderId,
  stageId,
}: {
  builderId: string;
  stageId: string;
}): string {
  return `${getBuilderDrawingIntakeStagesURL({ builderId, stageId })}/start`;
}

/** GET sheets for a job: /jobs/:jobId/sheets */
export function getBuilderDrawingIntakeJobSheetsURL({
  builderId,
  jobId,
}: {
  builderId: string;
  jobId: string;
}): string {
  return `${getBuilderURL(builderId)}/drawing-intake/jobs/${jobId}/sheets`;
}

/**
 * HOME PLANS
 */

/**
 * @description
 * - URL: /builders/:builderId/home-plans
 * - CONTROLLER: HomePlansController#getMany
 * - RESPONSE TYPE: <ClientPaginatedResponse<ClientHomePlan>>
 * @param builderId encoded builder id
 * @returns string
 */
function getBuilderHomePlansURL(builderId: string) {
  return `${baseBuilderURL}${builderId}/home-plans`;
}

function getBuilderHomePlanURL({ builderId, homePlanId }: { builderId: string; homePlanId: string }) {
  return `${getBuilderHomePlansURL(builderId)}/${homePlanId}`;
}

/**
 * @description
 * - URL: /builders/:builderId/home-plans/:homePlanId/studio-doc
 * - CONTROLLER: HomePlansController#getStudioDocMetadata
 * - RESPONSE TYPE: <ClientStudioDocMetadata>
 * @param builderId encoded builder id
 * @param homePlanId encoded home plan id
 * @returns string
 */
export function getStudioDocMetadataForHomePlanURL(builderId: string, homePlanId: string) {
  return `${getBuilderHomePlanURL({ builderId, homePlanId })}/studio-doc`;
}

/**
 * @description
 * - URL: /builders/:builderId/home-plans/:homePlanId
 * - CONTROLLER: HomePlansController#getOneHydrated
 * - RESPONSE TYPE: <ClientHomePlanHydrated>
 * @param builderId encoded builder id
 * @param homePlanId encoded home plan id
 * @param studioDocVersionId optional encoded studio doc version id — hydrates doc from that snapshot with current plan overrides
 * @returns string
 */
export function getHomePlanHydratedURL({
  builderId,
  homePlanId,
  studioDocVersionId,
}: {
  builderId: string;
  homePlanId: string;
  studioDocVersionId?: string;
}) {
  const base = getBuilderHomePlanURL({ builderId, homePlanId });
  if (studioDocVersionId) {
    const params = new URLSearchParams({ studioDocVersionId });
    return `${base}?${params.toString()}`;
  }
  return base;
}

/**
 * @description
 * - URL: /builders/:builderId/home-plans/:homePlanId/base-data
 * - CONTROLLER: HomePlansController#getOneBaseData
 * - RESPONSE TYPE: <ClientHomePlanBaseData>
 * @param builderId encoded builder id
 * @param homePlanId encoded home plan id
 * @returns string
 */
export function getHomePlanBaseDataURL({ builderId, homePlanId }: { builderId: string; homePlanId: string }) {
  return `${getBuilderHomePlanURL({ builderId, homePlanId })}/base-data`;
}

/**
 * @description
 * - URL: /builders/:builderId/master-plans/:masterPlanId/hydrated
 * - CONTROLLER: MasterPlansController#getOneHydrated
 * - RESPONSE TYPE: <ClientMasterPlanHydrated>
 * @param builderId encoded builder id
 * @param masterPlanId encoded master plan id
 * @param studioDocVersionId optional encoded studio doc version id — hydrates doc from that snapshot with master plan option templates
 * @returns string
 */
export function getMasterPlanHydratedURL({
  builderId,
  masterPlanId,
  studioDocVersionId,
}: {
  builderId: string;
  masterPlanId: string;
  studioDocVersionId?: string;
}) {
  const base = `${getBuilderURL(builderId)}/master-plans/${masterPlanId}/hydrated`;
  if (studioDocVersionId) {
    const params = new URLSearchParams({ studioDocVersionId });
    return `${base}?${params.toString()}`;
  }
  return base;
}

/**
 * @description
 * - URL: /builders/:builderId/master-plans/:masterPlanId/home-plans
 * - CONTROLLER: MasterPlansController#getHomePlans
 * - RESPONSE TYPE: <ClientHomePlanHydratedNoDoc[]>
 * @param builderId encoded builder id
 * @param masterPlanId encoded master plan id
 * @returns string
 */
export function masterPlanHomePlansURL(builderId: string, masterPlanId: string) {
  return `${getBuilderURL(builderId)}/master-plans/${masterPlanId}/home-plans`;
}

/**
 * @description
 * - URL: /builders/:builderId/master-plans/:masterPlanId/community-plans
 * - CONTROLLER: MasterPlansController#getCommunityPlans
 * - RESPONSE TYPE: <ClientLocationPlan[]>
 * @param builderId encoded builder id
 * @param masterPlanId encoded master plan id
 * @param includeArchived optional boolean to include archived plans
 * @returns string
 */
export function getMasterPlanCommunityPlansURL({
  builderId,
  masterPlanId,
  includeArchived,
}: {
  builderId: string;
  masterPlanId: string;
  includeArchived?: boolean;
}) {
  return addParamsToURL(`${getBuilderURL(builderId)}/master-plans/${masterPlanId}/community-plans`, {
    ...(includeArchived === true ? { archived: "true" } : {}),
  });
}

/**
 * @description
 * - URL: /builders/:builderId/master-plans/:masterPlanId/option-catalog-history
 * - CONTROLLER: OptionCatalogHistoryController#getHistory
 * - RESPONSE TYPE: <ClientPaginatedResponse<ClientOptionChangeEvent>>
 * @param builderId encoded builder id
 * @param masterPlanId encoded master plan id
 * @param filters optional filters
 * @returns string
 */
export function getMasterPlanOptionCatalogHistoryURL(
  builderId: string,
  masterPlanId: string,
  filters?: {
    startDate?: string;
    endDate?: string;
    optionSetId?: string;
    changeType?: string;
  },
): string {
  return addParamsToURL(`${baseBuilderURL}${builderId}/master-plans/${masterPlanId}/option-catalog-history`, {
    startDate: filters?.startDate,
    endDate: filters?.endDate,
    optionSetId: filters?.optionSetId,
    changeType: filters?.changeType,
  });
}

/**
 * @description
 * - URL: /builders/:builderId/master-plans/:masterPlanId/option-catalog-history/filters
 * - CONTROLLER: OptionCatalogHistoryController#getFilters
 * - RESPONSE TYPE: <ClientOptionCatalogHistoryFilters>
 * @param builderId encoded builder id
 * @param masterPlanId encoded master plan id
 * @returns string
 */
export function getMasterPlanOptionCatalogHistoryFiltersURL(builderId: string, masterPlanId: string): string {
  return `${baseBuilderURL}${builderId}/master-plans/${masterPlanId}/option-catalog-history/filters`;
}

/**
 * STUDIO DOCS
 */
const baseStudioDocURL = `${baseAPIPath}studio-docs/`;

/**
 * @description catalog-managed option data for a studio doc.
 * - URL: /studio-docs/:studioDocId/catalog-options?builderId=:builderId
 * - CONTROLLER: StudioDocsController#getCatalogOptions
 * - RESPONSE TYPE: <StudioDocCatalogOptionData>
 */
export function getStudioDocCatalogOptionsURL(studioDocId: string, builderId: string): string {
  return `${baseStudioDocURL}${studioDocId}/catalog-options?builderId=${builderId}`;
}

/**
 * @description
 * - URL: /builders/:builderId/studio-docs
 * - CONTROLLER: BuildersController#getStudioDocs
 * - RESPONSE TYPE: <ClientStudioDocMetadataForBuilder[]>
 * @param builderId encoded builder id
 * @param queryParams optional query parameters
 * @returns string
 */
export function getBuilderStudioDocsURL(builderId: string, queryParams?: { [x: string]: any }) {
  let url = `${baseBuilderURL}${builderId}/studio-docs`;

  if (queryParams) {
    url += objectToQueryParams(queryParams);
  }

  return url;
}

/**
 * @description
 * - URL: /builders/:builderId/admin/procure-workbooks
 * - CONTROLLER: BuildersController#getProcureWorkbooksForAdmin
 * - RESPONSE TYPE: <ClientProcureWorkbook[]>
 * @param builderId encoded builder id
 * @returns string
 */
export function getBuilderProcureWorkbooksForAdminURL(builderId: string) {
  return `${baseBuilderURL}${builderId}/admin/procure-workbooks`;
}

/**
 * @description
 * - URL: /builders/:builderId/studio-doc-events
 * - CONTROLLER: BuildersController#getStudioDocEvents
 * - RESPONSE TYPE: <ClientPaginatedResponse<ClientStudioDocEvent>>
 * @param builderId encoded builder id
 * @param filters optional filters for studioDocId and userId
 * @returns string
 */
export function getBuilderStudioDocEventsURL(builderId: string, filters?: { studioDocId?: string; userId?: string }) {
  const url = `${baseBuilderURL}${builderId}/studio-doc-events`;
  const params = new URLSearchParams();
  if (filters?.studioDocId) {
    params.set("studioDocId", filters.studioDocId);
  }
  if (filters?.userId) {
    params.set("userId", filters.userId);
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * @description
 * - URL: /builders/:builderId/studio-doc-event-filters
 * - CONTROLLER: BuildersController#getStudioDocEventFilters
 * - RESPONSE TYPE: <ClientStudioDocEventFilters>
 * @param builderId encoded builder id
 * @returns string
 */
export function getBuilderStudioDocEventFiltersURL(builderId: string) {
  return `${baseBuilderURL}${builderId}/studio-doc-event-filters`;
}

/**
 * @description
 * - URL: /studio-docs
 * - CONTROLLER: StudioDocsController#getMany
 * - RESPONSE TYPE: <ClientPaginatedResponse<ClientStudioDocMetadataForAdmin>>
 * @param params query parameters
 * @returns string
 */
export function getStudioDocsMetaDataForAdminURL(params: {
  hideUntitled?: boolean;
  userId?: string;
  builderId?: string;
}): string {
  return addParamsToURL(`${baseStudioDocURL}`, {
    hideUntitled: params.hideUntitled?.toString(),
    userId: params.userId,
    builderId: params.builderId,
  });
}

/**
 * @description
 * - URL: /studio-docs/:studioDocId
 * - CONTROLLER: StudioDocsController#getOne
 * - RESPONSE TYPE: <ClientStudioDocHydrated>
 * @param studioDocId encoded studio doc id
 * @param builderId optional encoded builder id
 * @returns string
 */
export function getStudioDocURL(
  studioDocId: string,
  builderId?: string,
  params: { includeStudioDocUsers?: boolean; includeStudioDocBuilders?: boolean; includeEstimationTask?: boolean } = {},
): string {
  return addParamsToURL(`${baseStudioDocURL}${studioDocId}`, {
    builderId,
    includeStudioDocUsers: params.includeStudioDocUsers?.toString(),
    includeStudioDocBuilders: params.includeStudioDocBuilders?.toString(),
    includeEstimationTask: params.includeEstimationTask?.toString(),
  });
}

/**
 * @description
 * - URL: /studio-docs/:studioDocId/studio-lite/workflow-state
 * - CONTROLLER: StudioLiteWorkflowStateController
 * - RESPONSE TYPE: <ClientStudioLiteWorkflowStateResponse> (GET), <ClientStudioLiteWorkflowState> (PUT)
 * @param studioDocId encoded studio doc id
 * @param builderId encoded builder id
 */
export function getStudioLiteWorkflowStateURL(studioDocId: string, builderId: string): string {
  return addParamsToURL(`${baseStudioDocURL}${studioDocId}/studio-lite/workflow-state`, { builderId });
}

/**
 * @description
 * - URL: /studio-docs/:studioDocId/meta-data
 * - CONTROLLER: StudioDocsController#getMetaData
 * - RESPONSE TYPE: <ClientStudioDocMetadata>
 * @param studioDocId encoded studio doc id
 * @param builderId optional encoded builder id
 * @returns string
 */
export function getHomePlanMetadataForStudioDocURL(studioDocId: string, builderId?: string): string {
  return `${baseStudioDocURL}${studioDocId}/meta-data${builderId ? `?builderId=${builderId}` : ""}`;
}

/**
 * @description
 * - URL: /studio-docs/:studioDocId/home-plans
 * - CONTROLLER: StudioDocsController#getHomePlans
 * - RESPONSE TYPE: <ClientPaginatedResponse<ClientHomePlan>>
 * @param studioDocId encoded studio doc id
 * @returns string
 */
export function getStudioDocHomePlansURL({
  studioDocId,
  builderId,
  pagination,
}: {
  studioDocId: string;
  builderId?: string;
  pagination?: PaginationParams;
}): string {
  return addParamsToURL(`${baseStudioDocURL}${studioDocId}/home-plans`, {
    ...(pagination ? convertPaginationParams(pagination) : undefined),
    builderId,
  });
}

/**
 * @description
 * - URL: /studio-docs/:studioDocId/home-plan-option-choices-sample
 * - CONTROLLER: StudioDocsController#getHomePlanOptionChoicesSample
 * - RESPONSE TYPE: <OptionChoices[]>
 * @param studioDocId encoded studio doc id
 * @param builderId optional encoded builder id
 * @returns string
 */
export function getStudioDocHomePlanOptionChoicesURL({
  studioDocId,
  builderId,
}: {
  studioDocId: string;
  builderId?: string;
}): string {
  return addParamsToURL(`${baseStudioDocURL}${studioDocId}/home-plan-option-choices-sample`, {
    builderId,
  });
}

interface OrderArgs {
  orderBy: string;
  order: "asc" | "desc";
}

/**
 * VERSIONS
 */
/**
 * @description
 * - URL: /studio-docs/:studioDocId/versions/paginated
 * - CONTROLLER: StudioDocsController#getVersions
 * - RESPONSE TYPE: <ClientStudioDocVersion[]>
 * @param studioDocId encoded studio doc id
 * @param builderId optional encoded builder id
 * @param pageLimit optional page limit, default is 1000
 * @param orderBy optional order arguments
 * @param isRefable optional boolean to filter refable versions
 * @returns string
 */
export function getStudioDocVersionsURL(
  studioDocId: string,
  builderId?: string,
  pageLimit = 1000,
  orderBy?: OrderArgs,
  isRefable?: boolean,
): string {
  let url = `${baseStudioDocURL}${studioDocId}/versions/paginated?limit=${pageLimit}`;

  if (builderId) {
    url += `&builderId=${builderId}`;
  }

  if (!!isRefable) {
    url += `&isRefable=true`;
  }

  if (orderBy) {
    url += `&orderBy=${orderBy.orderBy}&order=${orderBy.order}`;
  }

  return url;
}

/**
 * OPTION SETS/OPTIONS
 */

export function getBuilderOptionSetURL(builderId: string) {
  return `${baseBuilderURL}${builderId}/builder-option-sets`;
}

export function getOptionCatalogsMigrationURL(builderId: string) {
  return `${baseBuilderURL}${builderId}/option-catalogs-migration`;
}

export function getAllOptionCatalogsMigrationURL() {
  return `${baseAPIPath}option-catalogs-migration`;
}

/**
 * @description
 * - URL: /builders/:builderId/option-catalog/search-metadata
 * - CONTROLLER: OptionCatalogController#searchMetadata
 * - RESPONSE TYPE: <ClientOptionCatalogSearchMetadata>
 * @param builderId encoded builder id
 * @returns string
 */
export function getOptionCatalogSearchMetadataURL(builderId: string) {
  return `${baseBuilderURL}${builderId}/option-catalog/search-metadata`;
}

/**
 * BUILDER OPTION SETS (Next Gen Options)
 */

const baseBuilderOptionsUrl = (builderId: string) => `${baseBuilderURL}${builderId}/builder-options`;

/**
 * @description
 * - URL: /builders/:builderId/builder-options
 * - CONTROLLER: BuilderOptionController#getMany
 * - RESPONSE TYPE: <ClientBuilderOption[]>
 * @param builderId encoded builder id
 * @returns string
 */
export function getBuilderOptionsURL(builderId: string) {
  return baseBuilderOptionsUrl(builderId);
}

/**
 * PROCURE
 */

const baseProcureUrl = `${baseAPIPath}procure`;

/**
 * @description
 * - URL: /procure/cost-codes/upload
 * - CONTROLLER: ProcureController#uploadCostCodes
 * - RESPONSE TYPE: <void>
 * @param builderId encoded builder id
 * @returns string
 */
export function getProcureCostCodeUploadUrl(builderId: string): string {
  return `${baseProcureUrl}/cost-codes/upload?builderId=${builderId}`;
}

/**
 * @description
 * - URL: /procure/resources/upload
 * - CONTROLLER: ProcureController#uploadResources
 * - RESPONSE TYPE: <void>
 * @param builderId encoded builder id
 * @returns string
 */
export function getProcureResourceUploadUrl(builderId: string): string {
  return `${baseProcureUrl}/resources/upload?builderId=${builderId}`;
}

/**
 * @description
 * - URL: /builders/:builderId/procure-components
 * - CONTROLLER: ProcureComponentsController#getMany
 * - RESPONSE TYPE: <ClientProcureComponent[]>
 * @param builderId encoded builder id
 * @returns string
 */
export function getProcureComponentsURL({
  builderId,
  masterPlanId,
  communityPlanId,
  homePlanId,
}: {
  builderId: string;
  masterPlanId?: string;
  communityPlanId?: string;
  homePlanId?: string;
}) {
  return addParamsToURL(`${baseBuilderURL}${builderId}/procure-components`, {
    masterPlanId,
    communityPlanId,
    homePlanId,
  });
}

/**
 * @description
 * - URL: /builders/:builderId/procure-workbooks/:procureWorkbookId/measure-group-queries
 * - CONTROLLER: ProcureWorkbooksController#getMeasureGroupQueries
 * - RESPONSE TYPE: <ClientMeasureGroupQuery[]>
 * @param builderId encoded builder id
 * @param procureWorkbookId procure workbook id
 * @returns string
 */
export function getProcureWorkbookMeasureGroupsURL({
  builderId,
  procureWorkbookId,
}: {
  builderId: string;
  procureWorkbookId: string;
}) {
  return `${baseBuilderURL}${builderId}/procure-workbooks/${procureWorkbookId}/measure-group-queries`;
}
/**
 * MEASURE GROUP QUERIES
 * */

/**
 * @description
 * - URL: /builders/:builderId/measure-group-queries
 * - CONTROLLER: MeasureGroupQueriesController#getMany
 * - RESPONSE TYPE: <ClientMeasureGroupQuery[]>
 * @param builderId encoded builder id
 * @returns string
 * */
export function getMeasureGroupQueriesURL(builderId: string) {
  return `${baseBuilderURL}${builderId}/measure-group-queries`;
}

/**
 * INTEGRATIONS
 */

/**
 * @description
 * - URL: /builders/:builderId/integrations
 * - CONTROLLER: IntegrationsController#getMany
 * - RESPONSE TYPE: <ClientPaginatedResponse<ClientIntegration>>
 * @param builderId encoded builder id
 * @returns string
 */
export function getBuilderIntegrationsURL(builderId: string) {
  return `${baseBuilderURL}${builderId}/integrations`;
}

/**
 * USERS
 */

/**
 * @description
 * - URL: /builders/:builderId/users
 * - CONTROLLER: BuilderUsersController#getMany
 * - RESPONSE TYPE: <ClientUserHydrated[]>
 * @param builderId encoded builder id
 */

export function getBuilderUsersURL({ builderId }: { builderId: string }): string {
  return `${baseBuilderURL}${builderId}/users-internal`;
}

/**
 * PERMISSIONS
 */

/**
 * @description
 * - URL: /builders/:builderId/permissions/roles
 * - CONTROLLER: PermissionsController#listRoles
 * - RESPONSE TYPE: <ClientBuilderRole[]>
 * @param builderId encoded builder id
 */
export function getBuilderRolesURL({ builderId }: { builderId: string }): string {
  return `${baseBuilderURL}${builderId}/permissions/roles`;
}

/**
 * - METHOD: GET (list), POST (create)
 * - URL: /builders/:builderId/permissions/policies
 * - CONTROLLER: PermissionsController#listPolicies, PermissionsController#createPolicy
 * - RESPONSE TYPE: <ClientCedarPolicy[]> (GET), <ClientCedarPolicy> (POST)
 * @param builderId encoded builder id
 */
export function getBuilderPoliciesURL({ builderId }: { builderId: string }): string {
  return `${baseBuilderURL}${builderId}/permissions/policies`;
}

// utils

interface PaginationParams {
  limit?: number;
  offset?: number;
  search?: string;
  order?: string;
  orderBy?: string;
}

export function addParamsToURL(url: string, params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const key in params) {
    if (typeof params[key] === "string") {
      search.set(key, params[key]);
    }
  }

  if (!search.toString()) {
    return url;
  }

  return `${url}?${search.toString()}`;
}

/**
 * AUTO RENDER STYLES
 */

/**
 * - URL: /builders/:builderId/auto-render-styles
 * - CONTROLLER: AutoRenderStylesController#getMany
 * - RESPONSE TYPE: <ClientRenderStyle[]>
 * @param builderId encoded builder id
 * @param search optional search query
 * @returns string
 */
export function getBuilderAutoRenderStylesURL({ builderId, search }: { builderId: string; search?: string }): string {
  return addParamsToURL(`${baseBuilderURL}${builderId}/auto-render-styles`, {
    search,
  });
}

/**
 * - URL: /builders/:builderId/auto-render-styles/:styleId
 * - CONTROLLER: AutoRenderStylesController#getOne
 * - RESPONSE TYPE: <ClientRenderStyleHydrated>
 * @param builderId encoded builder id
 * @param styleId encoded style id
 * @returns string
 */
export function getBuilderAutoRenderStyleURL({ builderId, styleId }: { builderId: string; styleId: string }): string {
  return `${baseBuilderURL}${builderId}/auto-render-styles/${styleId}`;
}

/**
 * AUTO RENDERS
 */

/**
 * - URL: /builders/:builderId/auto-renders
 * - CONTROLLER: AutoRendersController#getMany
 * - RESPONSE TYPE: <ClientAutoRender[]>
 * @param builderId encoded builder id
 * @returns string
 */
export function getBuilderAutoRendersURL({ builderId }: { builderId: string }): string {
  return `${baseBuilderURL}${builderId}/auto-renders`;
}

/**
 * - URL: /builders/:builderId/auto-renders/:autoRenderId
 * - CONTROLLER: AutoRendersController#getOne
 * - RESPONSE TYPE: <ClientAutoRenderHydrated>
 * @param builderId encoded builder id
 * @param autoRenderId encoded auto render id
 * @returns string
 */
export function getBuilderAutoRenderURL({
  builderId,
  autoRenderId,
}: {
  builderId: string;
  autoRenderId: string;
}): string {
  return `${baseBuilderURL}${builderId}/auto-renders/${autoRenderId}`;
}

export function getBuilderAutoRenderByItemIdURL({ builderId, itemId }: { builderId: string; itemId: string }): string {
  return `${baseBuilderURL}${builderId}/auto-renders/item/${itemId}`;
}

/**
 * - URL: /builders/:builderId/auto-renders/:autoRenderId/start
 * - CONTROLLER: AutoRendersController#startRender
 * - RESPONSE TYPE: <ClientAutoRender>
 * @param builderId encoded builder id
 * @param autoRenderId encoded auto render id
 * @returns string
 */
export function getBuilderAutoRenderStartURL({
  builderId,
  autoRenderId,
}: {
  builderId: string;
  autoRenderId: string;
}): string {
  return `${baseBuilderURL}${builderId}/auto-renders/${autoRenderId}/start`;
}

/**
 * - URL: /builders/:builderId/auto-renders/:autoRenderId/status
 * - CONTROLLER: AutoRendersController#getStatus
 * - RESPONSE TYPE: <ClientRenderStatusResponse>
 * @param builderId encoded builder id
 * @param autoRenderId encoded auto render id
 * @returns string
 */
export function getBuilderAutoRenderStatusURL({
  builderId,
  autoRenderId,
}: {
  builderId: string;
  autoRenderId: string;
}): string {
  return `${baseBuilderURL}${builderId}/auto-renders/${autoRenderId}/status`;
}

function convertPaginationParams(params: PaginationParams) {
  return {
    limit: params.limit?.toString(),
    offset: params.offset?.toString(),
    search: params.search,
    order: params.order,
    orderBy: params.orderBy,
  };
}

/**
 * - URL: /builders/:builderId/auto-renders/:autoRenderId/clone-to-asset/:docId
 * - CONTROLLER: AutoRendersController#cloneToAsset
 * - RESPONSE TYPE: string (S3 URL of cloned asset)
 * @param builderId encoded builder id
 * @param autoRenderId encoded auto render id
 * @param docId document id for asset path
 * @returns URL path for the clone-to-asset POST endpoint
 */
export function getCloneAutoRenderToAssetURL({
  builderId,
  autoRenderId,
  docId,
}: {
  builderId: string;
  autoRenderId: string;
  docId: string;
}): string {
  return `${baseBuilderURL}${builderId}/auto-renders/${autoRenderId}/clone-to-asset/${docId}`;
}

/**
 * DATA MIGRATIONS
 */

const baseDataMigrationURL = `${baseAPIPath}data-migrations/`;

/**
 * - URL: /data-migrations/scripts
 * - CONTROLLER: DataMigrationController#getScripts
 * - RESPONSE TYPE: <DataMigrationScript[]>
 * @returns string
 */
export function getDataMigrationScriptsURL(): string {
  return `${baseDataMigrationURL}scripts`;
}

/**
 * - URL: /data-migrations/scripts/status
 * - CONTROLLER: DataMigrationController#getScriptsStatus
 * - RESPONSE TYPE: <DataMigrationScriptsStatusResponse>
 * @returns string
 */
export function getDataMigrationScriptsStatusURL(): string {
  return `${baseDataMigrationURL}scripts/status`;
}

/**
 * - URL: /data-migrations/run
 * - CONTROLLER: DataMigrationController#run
 * - RESPONSE TYPE: <{ jobId: string }>
 * @returns string
 */
export function getDataMigrationRunURL(): string {
  return `${baseDataMigrationURL}run`;
}

/**
 * - URL: /data-migrations/queue
 * - CONTROLLER: DataMigrationController#queue
 * - RESPONSE TYPE: <QueueMigrationsResult>
 * @returns string
 */
export function getDataMigrationQueueURL(): string {
  return `${baseDataMigrationURL}queue`;
}

/**
 * - URL: /data-migrations/run-queued-job
 * - CONTROLLER: DataMigrationController#runQueuedJob
 * - RESPONSE TYPE: <{ message: string | undefined }>
 * @returns string
 */
export function getDataMigrationRunQueuedJobURL(): string {
  return `${baseDataMigrationURL}run-queued-job`;
}

/**
 * - URL: /data-migrations/jobs
 * - CONTROLLER: DataMigrationController#getJobs
 * - RESPONSE TYPE: <ClientJobHydrated[]>
 * @returns string
 */
export function getDataMigrationJobsURL(): string {
  return `${baseDataMigrationURL}jobs`;
}

/**
 * ESTIMATION TASKS
 */

/**
 * - URL: /builders/:builderId/estimation-tasks/:taskId/hydrated
 * - CONTROLLER: EstimationTasksController#getOneHydrated
 * - RESPONSE TYPE: <ClientEstimationTaskHydrated>
 * @param builderId encoded builder id
 * @param taskId task id
 * @returns string
 */
export function getBuilderEstimationTaskURL({ builderId, taskId }: { builderId: string; taskId: string }): string {
  return `${baseBuilderURL}${builderId}/estimation-tasks/${taskId}/hydrated`;
}

/**
 * - URL: /builders/:builderId/estimation-tasks/hydrated
 * - CONTROLLER: EstimationTasksController#getManyHydrated
 * - RESPONSE TYPE: <ClientEstimationTaskHydrated[]>
 * @param builderId encoded builder id
 * @param locationId encoded location id
 * @param includeChildren when true, also include jobs from descendant locations
 * @returns string
 */
export function getBuilderEstimationTasksURL({
  builderId,
  locationId,
  includeChildren,
}: {
  builderId: string;
  locationId?: string;
  includeChildren?: boolean;
}): string {
  return addParamsToURL(`${baseBuilderURL}${builderId}/estimation-tasks/hydrated`, {
    locationId,
    includeChildren: includeChildren?.toString(),
  });
}

/**
 * - URL: /builders/:builderId/estimation-tasks/:taskId/bom
 * - CONTROLLER: EstimationTasksController#attachBom
 * - RESPONSE TYPE: <ClientEstimationTaskHydrated>
 * @param builderId encoded builder id
 * @param taskId task id
 * @returns string
 */
export function getBuilderEstimationTaskBomURL({ builderId, taskId }: { builderId: string; taskId: string }): string {
  return `${baseBuilderURL}${builderId}/estimation-tasks/${taskId}/bom`;
}

/**
 * - URL: /builders/:builderId/estimation-tasks/:taskId/pdf
 * - CONTROLLER: EstimationTasksController#attachPdf
 * - RESPONSE TYPE: <ClientEstimationTaskHydrated>
 * @param builderId encoded builder id
 * @param taskId task id
 * @returns string
 */
export function getBuilderEstimationTaskPdfURL({ builderId, taskId }: { builderId: string; taskId: string }): string {
  return `${baseBuilderURL}${builderId}/estimation-tasks/${taskId}/pdf`;
}

/**
 * - URL: /builders/:builderId/estimation-tasks/:taskId/studio-doc
 * - CONTROLLER: EstimationTasksController#attachStudioDoc
 * - RESPONSE TYPE: <ClientEstimationTaskHydrated>
 * @param builderId encoded builder id
 * @param taskId estimation task id
 * @returns string
 */
export function getBuilderEstimationTaskStudioDocURL({
  builderId,
  taskId,
}: {
  builderId: string;
  taskId: string;
}): string {
  return `${baseBuilderURL}${builderId}/estimation-tasks/${taskId}/studio-doc`;
}

/**
 * - URL: /builders/:builderId/estimation-tasks/:taskId/artifact-status
 * - CONTROLLER: EstimationTasksController#updateArtifactStatus
 * - RESPONSE TYPE: <ClientEstimationTask>
 * @param builderId encoded builder id
 * @param taskId estimation task id
 * @returns string
 */
export function getBuilderEstimationTaskArtifactStatusURL({
  builderId,
  taskId,
}: {
  builderId: string;
  taskId: string;
}): string {
  return `${baseBuilderURL}${builderId}/estimation-tasks/${taskId}/artifact-status`;
}

/**
 * - URL: /builders/:builderId/estimation-tasks/:taskId/workbook
 * - CONTROLLER: EstimationTasksController#attachWorkbook
 * - RESPONSE TYPE: <ClientEstimationTask>
 * @param builderId encoded builder id
 * @param taskId estimation task id
 * @returns string
 */
export function getBuilderEstimationTaskWorkbookURL({
  builderId,
  taskId,
}: {
  builderId: string;
  taskId: string;
}): string {
  return `${baseBuilderURL}${builderId}/estimation-tasks/${taskId}/workbook`;
}

/**
 * OPTION CHOICES
 */

export function getBuilderOptionChoicesURL({ builderId, taskId }: { builderId: string; taskId?: string }): string {
  return addParamsToURL(`${baseBuilderURL}${builderId}/builder-option-choices`, {
    estimationTaskId: taskId,
  });
}

export function getFileURL(file: string): string {
  return `${baseAPIPath}files/get-file?file=${file}`;
}
